import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../components/ui/form";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import Spinner from "../../components/ui/spinner";
import { useCreateAgentMutation } from "../../data/mutations/create-agent";
import { useUpdateAgentMutation } from "../../data/mutations/update-agent";
import { Agent } from "tekkr-common/dist/model/agent/agent";
import { IconPicker } from "../../components/icon-picker";

type PropsType = React.PropsWithChildren & {
    onSuccess?: () => void;
    editMode?: {
        agent: Agent;
    };
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
};

const formSchema = z.object({
    name: z.string().min(1, "Please enter a name").max(100, "This name is too long"),
    description: z.string().max(500, "This description is too long"),
    systemPrompt: z.string().min(1, "Please enter system instructions"),
    icon: z.string().optional(),
});

type FormType = z.infer<typeof formSchema>;

function getErrorMessage(e: Error, isEditMode: boolean) {
    return isEditMode
        ? "Updating this agent failed, please try again later."
        : "Creating this agent failed, please try again later.";
}

function AgentFormDialogContent(props: PropsType & { updateKey: () => void }) {
    const isEditMode = !!props.editMode;

    const createMutation = useCreateAgentMutation({
        onSuccess: () => {
            resetDialog();
            props.onSuccess?.();
        },
    });

    const updateMutation = useUpdateAgentMutation({
        onSuccess: () => {
            resetDialog();
            props.onSuccess?.();
        },
    });

    const mutation = isEditMode ? updateMutation : createMutation;

    const form = useForm<FormType>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            name: props.editMode?.agent.name ?? "",
            description: props.editMode?.agent.description ?? "",
            systemPrompt: props.editMode?.agent.systemPrompt ?? "",
            icon: props.editMode?.agent.icon ?? "",
        },
    });

    // Update form values when agent data becomes available
    useEffect(() => {
        if (props.editMode?.agent) {
            form.reset({
                name: props.editMode.agent.name,
                description: props.editMode.agent.description,
                systemPrompt: props.editMode.agent.systemPrompt,
                icon: props.editMode.agent.icon,
            });
        }
    }, [props.editMode?.agent, form]);

    function onSubmit(values: FormType) {
        if (isEditMode) {
            updateMutation.mutate({
                agentId: props.editMode!.agent.id,
                input: values,
            });
        } else {
            createMutation.mutate(values);
        }
    }

    function resetDialog() {
        form.reset();
        props.updateKey();
    }

    const [open, setOpenState] = React.useState(false);
    const setOpen = (open: boolean) => {
        if (mutation.isPending) return;
        if (!open) {
            resetDialog();
        }
        setOpenState(open);
        props.onOpenChange?.(open);
    };

    return (
        <Dialog open={props.open ?? open} onOpenChange={(open) => setOpen(open)}>
            {props.children}
            <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                    <DialogTitle className={"text-start"}>{isEditMode ? "Edit Agent" : "Create New Agent"}</DialogTitle>
                    <DialogDescription className={"text-start"}>
                        {isEditMode
                            ? "Update agent configuration"
                            : "Create a custom agent with specific instructions and behavior"}
                    </DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                        <FormField
                            control={form.control}
                            name="name"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Name</FormLabel>
                                    <FormControl>
                                        <Input
                                            disabled={mutation.isPending}
                                            autoComplete={"off"}
                                            placeholder="My Custom Agent"
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="description"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Description</FormLabel>
                                    <FormControl>
                                        <Textarea
                                            disabled={mutation.isPending}
                                            placeholder="A brief description of what this agent does..."
                                            className="min-h-[80px]"
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="icon"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Icon (Optional)</FormLabel>
                                    <FormControl>
                                        <IconPicker
                                            value={field.value}
                                            onValueChange={field.onChange}
                                            disabled={mutation.isPending}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="systemPrompt"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>System Instructions</FormLabel>
                                    <FormControl>
                                        <Textarea
                                            disabled={mutation.isPending}
                                            placeholder="Enter the system prompt that defines this agent's behavior and capabilities..."
                                            className="min-h-[200px] font-mono text-sm"
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <div className={"flex flex-row justify-end gap-2 pt-4"}>
                            <Button
                                type={"button"}
                                variant={"outline"}
                                onClick={() => setOpen(false)}
                                disabled={mutation.isPending}
                            >
                                Cancel
                            </Button>
                            <Button type={"submit"} disabled={mutation.isPending}>
                                {!mutation.isPending && (isEditMode ? "Save changes" : "Create agent")}
                                {mutation.isPending && <Spinner />}
                            </Button>
                        </div>
                    </form>
                </Form>

                {mutation.isError ? (
                    <div className={"rounded-md border border-destructive px-4 py-2 text-sm text-destructive"}>
                        {getErrorMessage(mutation.error, isEditMode)}
                    </div>
                ) : null}
            </DialogContent>
        </Dialog>
    );
}

export default function AgentFormDialog(props: PropsType) {
    const [key, setKey] = React.useState(Math.random());
    return <AgentFormDialogContent key={key} updateKey={() => setKey(Math.random())} {...props} />;
}
